import { useEffect } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Inbox, Loader2, Sparkles, Shield, Zap } from 'lucide-react'

export function LoginPage() {
  const { user, isLoading, signIn } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (user && !isLoading) {
      navigate({ to: '/dashboard', replace: true })
    }
  }, [user, isLoading, navigate])

  const handleLogin = () => {
    signIn()
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="h-12 w-12 rounded-xl bg-primary/10 animate-pulse" />
            <Loader2 className="absolute inset-0 m-auto h-6 w-6 animate-spin text-primary" />
          </div>
          <p className="text-sm text-muted-foreground animate-pulse">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      
      {/* Grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px), 
                           linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
          backgroundSize: '64px 64px'
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo and branding */}
        <div className="mb-8 text-center animate-in-page">
          <div className="inline-flex items-center justify-center mb-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary/80 shadow-lg glow-primary">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 64 64"
                fill="none"
                className="h-10 w-10 text-primary-foreground"
              >
                <path
                  d="M18 18l12 14-12 14M46 18L34 32l12 14"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18 50h28"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Xero Inbox</h1>
          <p className="mt-1 text-muted-foreground">
            Achieve inbox zero with AI automation
          </p>
        </div>

        {/* Login card */}
        <Card className="border-border/50 shadow-xl animate-in-page" style={{ animationDelay: '100ms' }}>
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl">Welcome back</CardTitle>
            <CardDescription>
              Sign in to manage your email automation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-2">
            <Button 
              onClick={handleLogin} 
              className="w-full h-12 text-base font-semibold glow-primary" 
              size="lg"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Sign in with WorkOS
            </Button>
            
            <p className="text-center text-xs text-muted-foreground">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </CardContent>
        </Card>

        {/* Features */}
        <div className="mt-8 grid grid-cols-3 gap-4" style={{ animationDelay: '200ms' }}>
          <div className="flex flex-col items-center text-center animate-in-page" style={{ animationDelay: '150ms' }}>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 mb-2">
              <Inbox className="h-5 w-5 text-primary" />
            </div>
            <span className="text-xs font-medium">Inbox Zero</span>
            <span className="text-[10px] text-muted-foreground">Automated</span>
          </div>
          <div className="flex flex-col items-center text-center animate-in-page" style={{ animationDelay: '200ms' }}>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 mb-2">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <span className="text-xs font-medium">AI Actions</span>
            <span className="text-[10px] text-muted-foreground">Smart</span>
          </div>
          <div className="flex flex-col items-center text-center animate-in-page" style={{ animationDelay: '250ms' }}>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 mb-2">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <span className="text-xs font-medium">Secure</span>
            <span className="text-[10px] text-muted-foreground">Private</span>
          </div>
        </div>
      </div>
    </div>
  )
}
